import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, IconButton, Typography, Alert, CircularProgress,
    TextField, List, Link, ListItem, ListItemText, Divider
} from '@mui/material';

import { Close as CloseIcon, Upload as UploadIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { groupService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import axios from 'axios'
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';

const DropzoneArea = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: '12px',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.grey[50],
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.grey[100]
    }
}));

const ImportAssignUsersModal = ({ open, onClose, onSuccess }) => {
    const { showToast } = useToast();
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            const selectedFile = e.target.files[0];
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                setError('Please select a valid CSV file');
                return;
            }
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError('');
        }
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv']
        },
        multiple: false,
        onDrop: (acceptedFiles, fileRejections) => {
            if (fileRejections.length > 0) {
                setError('Please upload a valid CSV file only.');
                return;
            }

            const file = acceptedFiles[0];
            if (file) {
                setFile(file);
                setFileName(file.name);
                setError('');
            }
        }
    });
    const downloadUsersSampleCsv = async () => {
        try {
            setDownloadLoading(true);
            const response = await axios.get('/api/assets/files/assign_users_data.csv', {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'assign_users_data.csv');

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setDownloadLoading(false);
        } catch (error) {
            console.error('Download error:', error);
            showToast('Error downloading sample file. Please try again.', 'error');
            setDownloadLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setError('Please select a CSV file');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const formData = new FormData();
            formData.append('csvfile', file);

            const response = await groupService.importAssignUsers(formData);

            if (response.data.status === 'ok') {
                setResult(response.data);
                showToast(response.data.message, 'success');
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error importing users:', error);
            setError(error.response?.data?.message || 'Failed to import users');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setFileName('');
        setError('');
        setResult(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Upload CSV</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {result && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body1" gutterBottom>
                                {result.message}
                            </Typography>
                            {result.details.errors.length > 0 && (
                                <>
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                        Errors:
                                    </Typography>
                                    <List dense sx={{ maxHeight: '150px', overflow: 'auto' }}>
                                        {result.details.errors.map((error, index) => (
                                            <ListItem key={index}>
                                                <ListItemText primary={error} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Alert>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" gutterBottom>
                            Upload a CSV file with user data.
                        </Typography>
                    </Box>
                    {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                        >
                            Select File
                            <input
                                type="file"
                                accept=".csv"
                                hidden
                                onChange={handleFileChange}
                            />
                        </Button>
                        <TextField
                            value={fileName}
                            placeholder="No file selected"
                            disabled
                            variant="outlined"
                            size="small"
                            sx={{ ml: 2, flexGrow: 1 }}
                        />
                    </Box> */}
                    <DropzoneArea {...getRootProps()}>
                        <input {...getInputProps({ accept: '.csv' })} />
                        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            or click to browse
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            Supported formats: .csv
                        </Typography>
                    </DropzoneArea>

                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        {downloadLoading ? (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        ) : (
                            <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                        )}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={downloadUsersSampleCsv}
                            underline="hover"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                        >
                            Download Sample CSV
                        </Link>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !file}
                        startIcon={loading && <CircularProgress size={20} />}
                        id="importUsersBtn"
                    >
                        Import
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ImportAssignUsersModal;